/**
 * Workspace mode hook - extracted from legacy auth hooks
 * Handles workspace mode switching with localStorage persistence
 */

import { useState, useCallback, useMemo } from 'react';
import { useProgressiveAuth } from '@/hooks/auth/useProgressiveAuth';

export type WorkspaceMode = 'talent' | 'company' | 'recruiter' | 'internal' | 'nanny' | 'mother';

export const useWorkspaceMode = () => {
  const auth = useProgressiveAuth();
  
  // Workspace mode with localStorage persistence
  const [currentMode, setCurrentModeState] = useState<WorkspaceMode>(() => {
    const stored = localStorage.getItem('workspace_mode');
    return (stored as WorkspaceMode) || 'talent';
  });

  const setCurrentMode = useCallback((mode: WorkspaceMode) => {
    setCurrentModeState(mode);
    localStorage.setItem('workspace_mode', mode);
  }, []);
  
  const availableModes = useMemo(() => {
    const modes: WorkspaceMode[] = [];
    
    if (auth.hasTalentAccess) modes.push('talent');
    if (auth.hasCompanyAccess) modes.push('company');
    if (auth.hasRecruiterAccess) modes.push('recruiter');
    if (auth.hasInternalAccess) modes.push('internal');
    if (auth.hasNannyAccess) modes.push('nanny');
    if (auth.hasMotherAccess) modes.push('mother');
    
    return modes;
  }, [auth.hasInternalAccess, auth.hasCompanyAccess, auth.hasRecruiterAccess, auth.hasTalentAccess, auth.hasNannyAccess, auth.hasMotherAccess]);
  
  const defaultMode = useMemo(() => {
    if (availableModes.includes('talent')) return 'talent';
    if (availableModes.includes('company')) return 'company';
    if (availableModes.includes('recruiter')) return 'recruiter';
    if (availableModes.includes('internal')) return 'internal';
    if (availableModes.includes('nanny')) return 'nanny';
    if (availableModes.includes('mother')) return 'mother';
    return 'talent';
  }, [availableModes]);
  
  return useMemo(() => ({
    availableModes,
    defaultMode,
    canSwitchMode: availableModes.length > 1,
    currentMode,
    setCurrentMode
  }), [availableModes, defaultMode, currentMode, setCurrentMode]);
};